import { fillPath, pathParamSchema, route } from "./routes.js";
import { z } from "zod";
import { RESOURCE_SLUG_PATTERN, channelDisplayNameSchema, ringSchema } from "@zion/sdk-contracts";
//#region src/channels.ts
/**
* `/v1/release/channels` and `/v1/release/customer-channels/app-bindings` — the org's release
* channels, their canary rollouts, and the per-app channel mappings.
*
* Producers in core-api's `src/release/`: `ChannelController` serves fourteen
* `/v1/release/channels` routes and this file names thirteen of them;
* `GET /v1/release/channels/{id}/assignments` is outside this contract.
* `CustomerChannelsController` serves the three app-binding routes. Both paths sit outside
* `/v1/admin`, so this file writes them literally and uses no prefix constant.
*
* Permissions: `channels:read` on the channel reads, `channels:manage` on every channel write and
* every canary verb, `app-channel-binding:read` and `app-channel-binding:manage` on the bindings.
*
* Route order is load-bearing on both controllers and this file lists the routes in the order the
* controllers declare them. `GET /v1/release/channels/{id}` comes after every `{id}/…` route, and
* the three `app-bindings` routes come before `GET /v1/release/customer-channels/{id}`: declared
* after it, `GET .../app-bindings` would land on the by-id handler and its uuid schema would
* refuse the literal string, giving a 400 where a list belongs.
*
* Six shell-vs-controller disagreements, and the controller is the fact in each:
*
* 1. `deleteChannel` is typed `Promise<void>` in the shell; the route answers 200 with
*    `{ message: 'Channel deleted' }`.
* 2. The shell's `CustomerChannel` carries `rerouted` and `reroutedToName`. `enrichChannel` writes
*    neither. Those two members belong to the my-channels resolver, so on these two routes they
*    read `undefined` forever.
* 3. The shell's `CustomerChannel` omits `ring`, the canonical member, and types `ringName` as
*    optional and nullable; `enrichChannel` always sends all three of `ring`, `ringSubscription`
*    and `ringName`, non-null and equal.
* 4. The shell's `CustomerChannelRecord` omits `organizationId`, `dpEndpoint` and `createdBy`. The
*    create and update routes return the bare `release.customer_channels` row, which carries them.
* 5. The shell's `CreateChannelRequest` requires `slug` and declares no `ring` or `isDefault`. The
*    server accepts `slug` as optional and nullable, and accepts both other members.
* 6. The shell's `UpdateChannelRequest` types `ringSubscription` and `ringName` as nullable. The
*    server's schema is `z.string().optional()` on both, so sending null is a 400, and the service
*    rejects null explicitly because the column is `NOT NULL`.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client.
* No shape in this file travels in both directions.
*/
/** The channel collection. No parameter. */
const RELEASE_CHANNELS_PATH = "/v1/release/channels";
/** One channel, in OpenAPI `{param}` form. Fill it with {@link releaseChannelPath}. */
const RELEASE_CHANNEL_PATH_TEMPLATE = `${RELEASE_CHANNELS_PATH}/{id}`;
/** One channel's group assignments. Fill it with {@link releaseChannelAssignmentsPath}. */
const RELEASE_CHANNEL_ASSIGNMENTS_PATH_TEMPLATE = `${RELEASE_CHANNEL_PATH_TEMPLATE}/assignments`;
/** One channel's canary. Fill it with {@link releaseChannelCanaryPath}. */
const RELEASE_CHANNEL_CANARY_PATH_TEMPLATE = `${RELEASE_CHANNEL_PATH_TEMPLATE}/canary`;
/** The advance action. Fill it with {@link releaseChannelCanaryAdvancePath}. */
const RELEASE_CHANNEL_CANARY_ADVANCE_PATH_TEMPLATE = `${RELEASE_CHANNEL_CANARY_PATH_TEMPLATE}/advance`;
/** The pause action. Fill it with {@link releaseChannelCanaryPausePath}. */
const RELEASE_CHANNEL_CANARY_PAUSE_PATH_TEMPLATE = `${RELEASE_CHANNEL_CANARY_PATH_TEMPLATE}/pause`;
/** The resume action. Fill it with {@link releaseChannelCanaryResumePath}. */
const RELEASE_CHANNEL_CANARY_RESUME_PATH_TEMPLATE = `${RELEASE_CHANNEL_CANARY_PATH_TEMPLATE}/resume`;
/** The rollback action. Fill it with {@link releaseChannelCanaryRollbackPath}. */
const RELEASE_CHANNEL_CANARY_ROLLBACK_PATH_TEMPLATE = `${RELEASE_CHANNEL_CANARY_PATH_TEMPLATE}/rollback`;
/** One channel's live canaries, one per app. Fill it with {@link releaseChannelCanariesPath}. */
const RELEASE_CHANNEL_CANARIES_PATH_TEMPLATE = `${RELEASE_CHANNEL_PATH_TEMPLATE}/canaries`;
/** Every app channel mapping this org holds. A literal segment, no parameter. */
const RELEASE_APP_CHANNEL_BINDINGS_PATH = "/v1/release/customer-channels/app-bindings";
/** One app's mapping, in OpenAPI `{param}` form. Fill it with {@link releaseAppChannelBindingPath}. */
const RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE = `${RELEASE_APP_CHANNEL_BINDINGS_PATH}/{appId}`;
/**
* `GET /v1/release/channels`. Every channel for the caller's org, enriched with counts, assigned
* groups, app versions and health. Permission `channels:read`.
*/
const LIST_CHANNELS_ROUTE = route("GET", RELEASE_CHANNELS_PATH);
/**
* `POST /v1/release/channels`. Creates a channel; **201 with the bare row**, not the enriched
* view. Permission `channels:manage`. 400 `invalid_ring` when no ring alias is supplied or the
* value is outside `RINGS`, 400 when the org already has a default channel and the body sets
* `isDefault`, and 409 `channel_below_floor` when the ring is below the tenant minimum-ring floor.
* Each coded refusal body carries `error` equal to `code`, so the server's global exception filter
* sends it whole (core-api issue #4509).
*/
const CREATE_CHANNEL_ROUTE = route("POST", RELEASE_CHANNELS_PATH);
/**
* `PATCH /v1/release/channels/{id}`. Renames a channel or moves its ring; 200 with the bare row,
* 404 when the channel is not this org's. Permission `channels:manage`. Same `invalid_ring` and
* minimum-ring refusals as the create, whose coded bodies carry `error` equal to `code`.
*/
const UPDATE_CHANNEL_ROUTE = route("PATCH", RELEASE_CHANNEL_PATH_TEMPLATE);
/**
* `DELETE /v1/release/channels/{id}`. Deletes a channel; answers 200 with a message body, never
* 204. Permission `channels:manage`. 409 `assignments_exist` unless the delete is forced, with
* `{ error, code, channelId, assignmentCount, hint }`, where `error` equals `code`.
*/
const DELETE_CHANNEL_ROUTE = route("DELETE", RELEASE_CHANNEL_PATH_TEMPLATE);
/**
* `PUT /v1/release/channels/{id}/assignments`. Replaces the channel's group assignments and
* returns the resulting rows. A group can be assigned to one channel only, so this also clears
* each named group from any other channel. Permission `channels:manage`; 404 when the channel is
* not this org's.
*/
const SET_CHANNEL_ASSIGNMENTS_ROUTE = route("PUT", RELEASE_CHANNEL_ASSIGNMENTS_PATH_TEMPLATE);
/**
* `POST /v1/release/channels/{id}/canary`. Starts a canary of one app at one target version on the
* channel; 201 with the new {@link canaryStatusSchema} status. Permission `channels:manage`.
* The body is {@link startCanaryRequestSchema}; fill the path with {@link releaseChannelCanaryPath}.
*
* Its conflict and coverage errors are structured bodies, which Nest sends whole:
*
* - 409 `{ error, code: 'canary_already_live', canaryId, appId, state, hint }` when a running or
*   paused canary already exists for the app on this channel;
* - 400 `{ code: 'app_has_no_fixtures', appId, hint }` when the operator has synced no fixture
*   versions for the app;
* - 400 `{ code: 'incomplete_fixture_coverage', appId, version, missingFixtures, hint }` when some
*   fixture has no row at the target version;
* - 400 `{ code: 'fixture_requires_newer_dp', appId, version, dpVersion, blockedFixtures, hint }`
*   when some fixture has no row at the target version that runs on this data plane. A row runs
*   here when its floor is at or below this data plane's version. `dpVersion` is the data plane
*   version, or null when it is unknown.
*   `blockedFixtures` lists `{ fixtureName, minDpVersion }` per such fixture.
*
* Each coded body also carries `error` with the same value as `code`. The server's global
* exception filter passes a 4xx object body through only when its `error` key is a snake_case code
* (#4450); without it the client gets `{ statusCode, message, timestamp, path }` alone.
*
* A channel holds one live canary per app, so two apps can each run a canary on one channel.
*
* 404 `Channel not found` when the channel is not this org's. A client that reads only `message`
* discards `code` and `hint`, which are the parts an admin can act on.
*/
const START_CANARY_ROUTE = route("POST", RELEASE_CHANNEL_CANARY_PATH_TEMPLATE);
/**
* `POST /v1/release/channels/{id}/canary/advance`. Advances one app's rollout by one batch;
* 201 with the updated status. The only canary verb with a body. Permission `channels:manage`.
*
* The app is the {@link canaryAppQuerySchema} query, `?appId=<slug>`. Without it the route acts
* on the channel's one live canary, and refuses when two or more are live: see
* {@link canaryAppRequiredBodySchema}. With it, 404 when that app has no live canary on the
* channel.
*
* The body is {@link advanceCanaryRequestSchema}: `batchSize` is an integer from 1 to 1000. The
* server's validation pipe refuses any other `batchSize` before the service runs. The client then
* gets a 400 whose `message` is an array of strings, with no `code`. The service keeps its own
* `invalid_batch_size` guard, and an HTTP client never sees that body.
*
* Its other errors are structured bodies:
*
* - 409 `canary_not_running`;
* - 400 `incomplete_fixture_coverage`, with `missingFixtures[]`;
* - 400 `fixture_requires_newer_dp`, with `dpVersion` and `blockedFixtures[]`, the body of
*   {@link START_CANARY_ROUTE}. The advance checks again. A data plane can roll back after the
*   start; the refused advance then writes no new override row. Rows from earlier advances stay;
* - 400 `app_has_no_fixtures`;
* - 409 `canary_app_required`.
*
* Each body carries `error` equal to `code`, so the server's exception filter sends it whole. A
* caller that reads only `message` discards the content that an admin can act on.
*/
const ADVANCE_CANARY_ROUTE = route("POST", RELEASE_CHANNEL_CANARY_ADVANCE_PATH_TEMPLATE);
/**
* `POST /v1/release/channels/{id}/canary/pause`. Stops the advance of one app's rollout; 201 with
* the status. Migrated users keep the new version. Pause is not a revert, so "pause to
* investigate" destroys nothing. Permission `channels:manage`; 409 `invalid_canary_transition`.
* The app selector, the 404 and the 409 `canary_app_required` are those of
* {@link ADVANCE_CANARY_ROUTE}.
*/
const PAUSE_CANARY_ROUTE = route("POST", RELEASE_CHANNEL_CANARY_PAUSE_PATH_TEMPLATE);
/**
* `POST /v1/release/channels/{id}/canary/resume`. Resumes one app's paused canary; 201 with
* the status. Permission `channels:manage`; 409 `invalid_canary_transition`. The app selector, the
* 404 and the 409 `canary_app_required` are those of {@link ADVANCE_CANARY_ROUTE}.
*/
const RESUME_CANARY_ROUTE = route("POST", RELEASE_CHANNEL_CANARY_RESUME_PATH_TEMPLATE);
/**
* `POST /v1/release/channels/{id}/canary/rollback`. Rolls one app's canary back; 201 with the
* status. It removes the overrides that canary wrote, by provenance. So a manual override that an
* admin set by hand on the same fixture survives, and another app's canary keeps its overrides.
* Permission `channels:manage`; 409 `invalid_canary_transition`. The app selector, the 404 and the
* 409 `canary_app_required` are those of {@link ADVANCE_CANARY_ROUTE}.
*/
const ROLLBACK_CANARY_ROUTE = route("POST", RELEASE_CHANNEL_CANARY_ROLLBACK_PATH_TEMPLATE);
/**
* `GET /v1/release/channels/{id}/canary`. One live canary's status, or 204 and no body when
* none matches. Permission `channels:read`; 404 when the channel is not this org's.
*
* With the {@link canaryAppQuerySchema} query, `?appId=<slug>`, it answers that app's live canary,
* or 204. Without it, it answers the channel's oldest live canary: the first by `startedAt`, then
* by `appId`. It never refuses an ambiguous read; {@link LIST_CANARIES_ROUTE} lists every live
* canary on the channel.
*/
const GET_CANARY_STATUS_ROUTE = route("GET", RELEASE_CHANNEL_CANARY_PATH_TEMPLATE);
/**
* `GET /v1/release/channels/{id}/canaries`. Every live (`running` or `paused`) canary on the
* channel, one per app, ordered by `startedAt` then `appId`; 200 with
* {@link listCanariesResponseSchema}, an empty list when none is live. Permission
* `channels:read`; 404 when the channel is not this org's.
*
* Each item's `appId` is the value the status read and the four controls take as `?appId`.
*/
const LIST_CANARIES_ROUTE = route("GET", RELEASE_CHANNEL_CANARIES_PATH_TEMPLATE);
/**
* `GET /v1/release/channels/{id}`. One channel, enriched. Declared after every `{id}/…` route on
* the controller so `assignments` and `canary` are not read as an id. Permission `channels:read`.
*/
const GET_CHANNEL_ROUTE = route("GET", RELEASE_CHANNEL_PATH_TEMPLATE);
/**
* `GET /v1/release/customer-channels/app-bindings`. Every app channel mapping this org holds,
* sorted by app slug. Permission `app-channel-binding:read`.
*
* ⚠️ **There is no per-app GET, and that is the contract rather than an omission.** The shell's
* `getAppChannelBinding(appId)` reads this list route and filters client-side, which keeps the
* onboarding wizard's request count at three. A `GET` against `app-bindings/{appId}` is a 404: the
* path is registered for `PUT` and `DELETE` only.
*/
const LIST_APP_CHANNEL_BINDINGS_ROUTE = route("GET", RELEASE_APP_CHANNEL_BINDINGS_PATH);
/**
* `PUT /v1/release/customer-channels/app-bindings/{appId}`. Maps one app to one customer channel,
* replacing whatever mapping it had; 200 with the resulting mapping. Permission
* `app-channel-binding:manage`.
*
* Replaces and never appends: the relation's key is `(organization_id, app_id,
* customer_channel_id)`, so a server that inserted would leave two rows for one app and the
* resolver would then pick between them arbitrarily.
*
* The refusals:
* - 404 for a channel that is not this org's, an unknown app, or an app the org is not entitled
*   to. Each of the three is a 404 and never a 403, so a caller cannot probe for another org's
*   channels;
* - 403 `private_channel_not_accepted` for a private Customer Channel of this org with no
*   accepted, unrevoked offer. The body is `{ error, code, message }`, where `error` equals `code`;
* - 409 `channel_below_floor` when the channel's ring is below the org's minimum-ring policy. The
*   body is `{ error, code, floor, requestedRing }`, where `error` equals `code`.
*/
const PUT_APP_CHANNEL_BINDING_ROUTE = route("PUT", RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE);
/**
* `DELETE /v1/release/customer-channels/app-bindings/{appId}`. Removes one app's mapping so the
* app falls back to the org's default channel; 204 and no body. Permission
* `app-channel-binding:manage`; 404 for an unknown app, an unentitled app, or no mapping to
* remove.
*/
const DELETE_APP_CHANNEL_BINDING_ROUTE = route("DELETE", RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE);
/**
* Fill {@link RELEASE_CHANNEL_PATH_TEMPLATE} with one channel id.
*
* @param id the `release.customer_channels` uuid. The controller runs `ParseUUIDPipe` on
*   `@Param('id')` since #3332 and answers 400 for any other value. This builder keeps the wider
*   path-param grammar the 0.4.x releases accepted, so a non-uuid passes here and is refused by
*   the server. Throws when it is empty, over 128 characters, or outside the path-param grammar.
*/
function releaseChannelPath(id) {
	return fillPath(RELEASE_CHANNEL_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_ASSIGNMENTS_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelAssignmentsPath(id) {
	return fillPath(RELEASE_CHANNEL_ASSIGNMENTS_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARY_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanaryPath(id) {
	return fillPath(RELEASE_CHANNEL_CANARY_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARY_ADVANCE_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanaryAdvancePath(id) {
	return fillPath(RELEASE_CHANNEL_CANARY_ADVANCE_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARY_PAUSE_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanaryPausePath(id) {
	return fillPath(RELEASE_CHANNEL_CANARY_PAUSE_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARY_RESUME_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanaryResumePath(id) {
	return fillPath(RELEASE_CHANNEL_CANARY_RESUME_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARY_ROLLBACK_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanaryRollbackPath(id) {
	return fillPath(RELEASE_CHANNEL_CANARY_ROLLBACK_PATH_TEMPLATE, { id });
}
/** Fill {@link RELEASE_CHANNEL_CANARIES_PATH_TEMPLATE}. Same bound as {@link releaseChannelPath}. */
function releaseChannelCanariesPath(id) {
	return fillPath(RELEASE_CHANNEL_CANARIES_PATH_TEMPLATE, { id });
}
/**
* Fill {@link RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE} with one app id.
*
* @param appId the `release.apps_catalog` uuid, which is what the route's
*   `appBindingParamsSchema` requires. Not the slug: a non-uuid is a 400 before any query runs.
*/
function releaseAppChannelBindingPath(appId) {
	return fillPath(RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE, { appId });
}
/** One group assigned to a channel, with its name and its direct member count. */
const assignedGroupInfoSchema = z.object({
	groupId: z.string(),
	/** `'Unknown'` when the group row is gone; the service substitutes that literal. */
	groupName: z.string(),
	/**
	* Direct principal members only, not the flattened descendant closure. This is a per-group
	* property; the channel's own `userCount` is the deduplicated closure.
	*/
	memberCount: z.number().int().nonnegative()
});
/** The latest version of one app on the channel's ring. */
const channelAppVersionSchema = z.object({
	/** The fixture's `app_id`, falling back to its `fixture_name` when the app id is null. */
	appId: z.string(),
	/** Derived from `appId` by title-casing it; not a stored display name. */
	appName: z.string(),
	currentVersion: z.string(),
	/** Always null today: `enrichChannel` writes no target version. */
	targetVersion: z.string().nullable()
});
/**
* The channel's health, derived from its state rather than stored. `enrichChannel` writes
* `'frozen'` when assignments are paused and `'healthy'` otherwise; `'degraded'` and
* `'upgrading'` are in the vocabulary and have no writer on this route.
*/
const channelHealthSchema = z.object({
	status: z.enum([
		"healthy",
		"degraded",
		"upgrading",
		"frozen"
	]),
	message: z.string().optional()
});
/**
* The enriched channel — the response of `GET /v1/release/channels` and
* `GET /v1/release/channels/{id}` and of those two only.
*
* `ChannelService.listChannels` and `getChannel` run every row through `enrichChannel`, which is
* what adds the ring aliases, `isFrozen`, the two counts, `assignedGroups`, `appVersions` and
* `health`. The create and update routes return the bare row and have none of them — see
* {@link customerChannelRecordSchema}. Typing all four alike made seven members read `undefined`
* on the two write paths.
*
* Named `AdminCustomerChannel` because `@zion/sdk-contracts` exports a `CustomerChannel` that is a
* different shape: that one is the DP row as the CP observes it, carrying `orgId` and a single
* `ring`, with none of the enrichment here.
*/
const adminCustomerChannelSchema = z.object({
	id: z.string(),
	name: z.string(),
	/** Nullable: `release.customer_channels.slug` has no `NOT NULL`. */
	slug: z.string().nullable(),
	/** The canonical ring member. */
	ring: ringSchema,
	/** Back-compat alias for older shell clients. Always the same value as `ring`. */
	ringSubscription: ringSchema,
	/** Back-compat alias for older shell clients. Always the same value as `ring`. */
	ringName: ringSchema,
	isDefault: z.boolean(),
	/**
	* Derived from `pauseAssignments`, not from a null ring. The legacy freeze-via-null-ring hack is
	* gone: the column is `NOT NULL`.
	*/
	isFrozen: z.boolean(),
	pauseAssignments: z.boolean(),
	/**
	* Distinct users the channel serves, deduplicated. On the default channel it is the org's active
	* users minus everyone assigned elsewhere; on any other channel it is the flattened closure of
	* its assigned groups.
	*/
	userCount: z.number().int().nonnegative(),
	groupCount: z.number().int().nonnegative(),
	assignedGroups: z.array(assignedGroupInfoSchema),
	appVersions: z.array(channelAppVersionSchema),
	health: channelHealthSchema,
	description: z.string().nullable(),
	/**
	* The CP private channel this customer channel binds to, or null on a public-ring channel
	* (private-release-channels R-003). When set, `ring` is `edge`, the floor class (L-7).
	*/
	privateChannelId: z.string().nullable(),
	/**
	* The private channel's routing key (`p-` and 12 hex digits), or null on a public-ring channel.
	* Sign-in mints the JWT `channel` claim from it, so users assigned here route to the private
	* channel's pinned versions.
	*/
	privateChannelKey: z.string().nullable(),
	/**
	* Whether the caller may assign a group to this channel: the same access set the three
	* assignment routes enforce (`ChannelAudienceService.assignAccessOf`). An owner may assign any
	* channel; a holder of `channels:manage` any public channel (`privateChannelId` null); a holder
	* of `release_channel:assign` or `manage` on a channel, that channel. The creator of a channel
	* gets `manage` at create, and an owner can revoke it (channel-assign-rule R-001, R-004).
	* Display data only: every write is checked again on the server.
	*
	* Three limits. `true` is the resource-target check alone: the write can still refuse one group
	* with 409 `group_already_assigned` when that group sits in the audience of a channel the
	* caller cannot assign. Two of the three assignment routes also require the org permission
	* `channels:manage` ahead of that check: `PUT /v1/admin/groups/{id}/channel` and
	* `PUT /v1/release/channels/{id}/assignments`. A caller admitted by a grant alone reads `true`,
	* gets 403 `Missing required org permission: channels:manage` there, and 200 on
	* `PUT /v1/release/customer-channels/{id}/assignments`, which carries no org key. A consumer
	* that drives one of the first two routes also checks its permission snapshot for
	* `channels:manage` before it offers the assignment. An on-behalf-of or
	* delegated identity, a machine principal (an agent, service or worker token) and a token with
	* no `principalKind` claim (minted before WS-A.2) get no public-channel arm, so they read
	* `false` on a public channel they hold no grant on (channel-assign-rule R-006, R-008).
	*
	* Defaults to `false` when absent, so a client on this version that reads a core-api from
	* before the field offers no assignment rather than every one (channel-assign-rule R-002).
	*/
	canAssign: z.boolean().default(false),
	/** ISO-8601. The service holds a `Date` and serialises it. */
	createdAt: z.string(),
	/** ISO-8601. */
	updatedAt: z.string()
});
/**
* A bare `release.customer_channels` row — what `POST /v1/release/channels` and
* `PATCH /v1/release/channels/{id}` return.
*
* Un-enriched: no counts, no `assignedGroups`, no `appVersions`, no `health`, and the ring is
* `ringName` only. A caller that needs the enriched view re-reads the channel.
*
* `ringName` is a bare string and not `ringSchema`: the column is a `varchar(64)` and its
* four-value bound is a database `CHECK`, not a type. `organizationId`, `dpEndpoint` and
* `createdBy` are on the wire and absent from the shell's mirror of this shape.
*/
const customerChannelRecordSchema = z.object({
	id: z.string(),
	/** The owning org. On the wire, and absent from the shell's `CustomerChannelRecord`. */
	organizationId: z.string(),
	name: z.string(),
	/** One of the four rings, enforced by `customer_channels_ring_name_chk`. */
	ringName: z.string(),
	isDefault: z.boolean(),
	pauseAssignments: z.boolean(),
	description: z.string().nullable(),
	slug: z.string().nullable(),
	/** On the wire, and absent from the shell's mirror. Null on every row this route creates. */
	dpEndpoint: z.string().nullable(),
	/** The user that created the channel. On the wire, and absent from the shell's mirror. */
	createdBy: z.string().nullable(),
	/** The bound CP private channel, or null (private-release-channels R-003). */
	privateChannelId: z.string().nullable(),
	/** The bound private channel's routing key, or null (R-003). */
	privateChannelKey: z.string().nullable(),
	/** ISO-8601. */
	createdAt: z.string(),
	/** ISO-8601. */
	updatedAt: z.string()
});
/**
* A raw `release.channel_assignments` row — what `PUT /v1/release/channels/{id}/assignments`
* returns.
*
* There is no `channelName` here. The two-member `{ channelId, channelName }` projection is a
* different shape that appears nested inside the group-detail response, and typing this route with
* it made `channelName` render `undefined`.
*/
const channelGroupAssignmentSchema = z.object({
	id: z.string(),
	organizationId: z.string(),
	channelId: z.string(),
	groupId: z.string(),
	/** ISO-8601. */
	createdAt: z.string(),
	createdBy: z.string().nullable()
});
/**
* The canary wire contract — core-api's `CanaryStatusResponse`.
*
* The four states are `release.canary_migrations.state`'s own `CANARY_STATES`. The server once
* emitted `'active'`, which is not a member, so the vocabulary is pinned in one place there and
* mirrored here.
*/
const canaryStatusSchema = z.object({
	channelId: z.string(),
	channelName: z.string(),
	/** The app **slug**, not the `apps_catalog` uuid. */
	appId: z.string(),
	/**
	* Null while the channel's ring has no resolved version yet, during the operator sync window.
	* Render by dropping the clause ("Migrating to v2.0.0"), never with a placeholder: React prints
	* null as empty, and a `0.0.0` stand-in sends a reader looking for a version that never existed.
	*/
	fromVersion: z.string().nullable(),
	toVersion: z.string(),
	state: z.enum([
		"running",
		"paused",
		"completed",
		"rolled_back"
	]),
	/**
	* `migratedUsers` over `totalUsers`, rounded to a whole number from 0 to 100. A zero
	* `totalUsers` gives 0. The value never passes 100. It can go down at an advance, because the
	* advance adds to `totalUsers` the members who joined since the last start or advance. A join
	* alone changes neither value on a read.
	*/
	progressPercent: z.number(),
	migratedUsers: z.number().int().nonnegative(),
	/**
	* The migrated users plus the pending users, as of the last start or advance. A pending user
	* resolves to the channel, has no manual override on the app's fixtures and has not migrated.
	* The server writes the value at start and at each advance. A status read reports
	* `max(total_users, migratedUsers)`, so the value is never below `migratedUsers`.
	*/
	totalUsers: z.number().int().nonnegative(),
	/**
	* The users of this channel at the moment of the call who carry a manual version override on one
	* of the app's fixtures and have not migrated. The canary leaves their version alone: they are
	* ineligible. `totalUsers` leaves them out, so `progressPercent` can reach 100. A user of another
	* channel never counts. A migrated user counts once, as migrated.
	*
	* Show the count and the reason. With no count, an operator reads "8 of 8 migrated" while ten
	* people use the app, and has no way to find the other two.
	*/
	excludedUsers: z.number().int().nonnegative(),
	/**
	* ISO-8601: when the canary started. `excludedUsers` describes the channel members at the moment
	* of the call. `totalUsers` describes them as of the last start or advance.
	*/
	startedAt: z.string(),
	/** ISO-8601. */
	updatedAt: z.string()
});
/**
* One org's binding of one app to one of its customer channels.
*
* Two names, on purpose. The user-facing noun is "app channel mapping" on every surface a person
* reads; the wire and the code keep `channelBinding`. Never "release channels", which is a
* different concept at `/admin/channels`.
*
* `ringName` is derived server-side from the bound channel and is never accepted from a request
* body — see {@link putAppChannelBindingRequestSchema}.
*/
const appChannelBindingSchema = z.object({
	/** `release.apps_catalog.id` — the uuid, never the slug. */
	appId: z.string(),
	appSlug: z.string(),
	customerChannelId: z.string(),
	/** `release.customer_channels.name` — the tenant's own display name. */
	customerChannelName: z.string(),
	ringName: z.string(),
	/** ISO-8601. */
	boundAt: z.string()
});
/** `GET /v1/release/channels` response: every channel, enriched, unpaged. */
const listChannelsResponseSchema = z.array(adminCustomerChannelSchema);
/** `GET /v1/release/channels/{id}` response: one channel, enriched. */
const getChannelResponseSchema = adminCustomerChannelSchema;
/**
* `POST /v1/release/channels` body.
*
* `name` carries `channelDisplayNameSchema` from `@zion/sdk-contracts`, the tenant-authored
* display-name bound. Deliberately not the `^[a-z0-9-]{1,32}$` routing-key grammar, which bounds a
* different thing.
*
* Three ring aliases are accepted and normalised server-side to one canonical ring. When more than
* one is present and they disagree, `ring` wins; any value outside `RINGS` is 400 `invalid_ring`
* whichever alias carried it. Each alias is a bare string here because the server's own schema is,
* and the vocabulary check happens in `normaliseRingAliases`.
*
* `slug` is optional and nullable, which the shell's mirror of this shape gets wrong by requiring
* it. `isDefault` is accepted and absent from that mirror.
*
* `privateChannelId` (private-release-channels R-003) binds the new channel to a CP private channel
* the org has accepted, in place of a ring. It is mutually exclusive with all three ring aliases:
* the server sets the ring (`edge`, L-7) and answers a body carrying `privateChannelId` beside any
* alias with 400 `private_channel_ring_conflict`. The exclusion is the server's, not a refinement
* here, so the schema stays a plain object that core-api's `shell-body-parity` reader can compare.
* The server also answers 403 `private_channel_not_accepted` when no accepted, unrevoked offer
* covers the channel, 400 `private_channel_cannot_be_default` with `isDefault: true` (R-019), and
* 409 `channel_below_floor` when the org's floor is below `edge`.
*
* Each coded refusal body carries `error` equal to `code`, so the server's global exception filter
* sends it whole (core-api issue #4509).
*/
const createChannelRequestSchema = z.strictObject({
	name: channelDisplayNameSchema,
	/**
	* `RESOURCE_SLUG_PATTERN`, at most 64 characters (the column is `varchar(64)`); null or absent
	* leaves the slug unset. The `create_customer_channel` MCP tool binds the same schema, so the
	* tool and `POST /v1/release/channels` refuse one body alike.
	*/
	slug: z.string().max(64).regex(RESOURCE_SLUG_PATTERN).nullable().optional(),
	/** The canonical ring alias. Wins when two aliases disagree. */
	ring: z.string().optional(),
	/** Ring alias. */
	ringName: z.string().optional(),
	/** Ring alias. */
	ringSubscription: z.string().optional(),
	/** 400 when the org already has a default channel. Unset the existing one first. */
	isDefault: z.boolean().optional(),
	/** Pause new assignments on creation. Defaults to false at the service. */
	pauseAssignments: z.boolean().optional(),
	description: z.string().nullable().optional(),
	/**
	* The accepted CP private channel to bind, a uuid. Send no ring alias with it; the server
	* refuses the pair with 400 `private_channel_ring_conflict`.
	*/
	privateChannelId: z.uuid().optional()
});
/** `POST /v1/release/channels` response: the bare row, 201. */
const createChannelResponseSchema = customerChannelRecordSchema;
/**
* `PATCH /v1/release/channels/{id}` body. Every member optional; an omitted member leaves the
* stored value alone.
*
* No `isFrozen`: the server's schema does not declare it and the strict pipe turns any undeclared
* member into a 400. `isFrozen` is a response member; the write-side control is
* `pauseAssignments`.
*
* The ring aliases are `z.string().optional()`, not nullable. Sending null is a 400 under the
* strict pipe, and the service rejects it too because the column is `NOT NULL`.
*
* `privateChannelId` changes the source of the channel (plan 2026-10-21-channel-source-change,
* private-release-channels R-073). The channel id, name, slug, groups and grants stay.
*
* - Omitted: the source stays. On a public channel a ring alias moves the ring.
* - A uuid: the channel takes that accepted CP private channel. The server sets the ring to
*   `edge` and copies the routing key from the offer. The current id is no change, and the server
*   runs only the first two refusals below for it.
* - Null: the channel moves to a public ring. On a private channel the body must also name a ring
*   alias that meets the floor. On a public channel, null with a ring alias equals the alias alone.
*
* The server refusals for a uuid `privateChannelId`, in order:
*
* - a ring alias beside the uuid: 400 `private_channel_ring_conflict`;
* - the org default, or `isDefault: true`: 400 `private_channel_cannot_be_default`;
* - no accepted and unrevoked offer covers the uuid: 403 `private_channel_not_accepted`;
* - an offer key outside the grammar: 409 `private_channel_key_invalid`;
* - an org floor stricter than `edge`: 409 `channel_below_floor`.
*
* The server refusals for a null `privateChannelId` on a private channel:
*
* - no ring alias: 400 `invalid_ring`;
* - a ring below the floor: 409 `channel_below_floor`.
*
* The server refusals with `privateChannelId` omitted on a private channel:
*
* - a ring alias: 400 `private_channel_ring_immutable`;
* - `isDefault: true`: 400 `private_channel_cannot_be_default`.
*
* The other server refusals:
*
* - a ring alias outside the four rings: 400 `invalid_ring`;
* - a change of ring or private channel while a canary is live on the channel: 409
*   `canary_already_live` with `{ error, code, canaryId, appId, state, hint }`, where `error`
*   equals `code` so that the server's global exception filter sends the body whole;
* - `isDefault: true` while another channel is the org default: 400;
* - a name or slug that another channel of the org already uses: 409 `customer_channel_exists`.
*
* Each coded refusal body above carries `error` equal to `code` (core-api issue #4509).
*
* As on the create schema, the server owns these refusals, so the schema stays a plain strict
* object.
*/
const updateChannelRequestSchema = z.strictObject({
	name: channelDisplayNameSchema.optional(),
	slug: z.string().nullable().optional(),
	/** The canonical ring alias. Wins when two aliases disagree. */
	ring: z.string().optional(),
	/** Ring alias. Not nullable — see this schema's note. */
	ringName: z.string().optional(),
	/** Ring alias. Not nullable — see this schema's note. */
	ringSubscription: z.string().optional(),
	isDefault: z.boolean().optional(),
	pauseAssignments: z.boolean().optional(),
	description: z.string().nullable().optional(),
	/**
	* The new source: the uuid of an accepted CP private channel, or null for a public ring. Omit it
	* to keep the source. See this schema's note for the refusals.
	*/
	privateChannelId: z.uuid().nullable().optional()
});
/** `PATCH /v1/release/channels/{id}` response: the bare row. */
const updateChannelResponseSchema = customerChannelRecordSchema;
/**
* `DELETE /v1/release/channels/{id}` response. The route answers 200 with this body; the
* controller returns `{ message: 'Channel deleted' }` after the service call.
*/
const deleteChannelResponseSchema = z.object({ 
/** Human-readable confirmation. Not a machine-readable code. */
message: z.string() });
/**
* `PUT /v1/release/channels/{id}/assignments` body. The full desired set of group ids: the route
* replaces the channel's assignments rather than adding to them.
*/
const setChannelAssignmentsRequestSchema = z.strictObject({ groupIds: z.array(z.string()) });
/** `PUT /v1/release/channels/{id}/assignments` response: the resulting rows. */
const setChannelAssignmentsResponseSchema = z.array(channelGroupAssignmentSchema);
/**
* `POST /v1/release/channels/{id}/canary/advance` body.
*
* Bounded at both ends. `min(1)` because a zero-size advance returns 200 and changes nothing,
* which a caller cannot tell from a stalled rollout. `max(1000)` because each user costs one
* override row per fixture, so an unbounded batch on a many-fixture app is an unbounded write
* inside one transaction.
*/
const advanceCanaryRequestSchema = z.strictObject({ batchSize: z.coerce.number().int().min(1).max(1e3) });
/**
* `POST /v1/release/channels/{id}/canary` body.
*
* `appId` is the app **slug**, the same value {@link canaryStatusSchema}'s `appId` carries, and
* not the `apps_catalog` uuid. The service matches it against `release.fixture_versions.app_id`,
* which holds slugs, so `pathParamSchema`'s one-segment grammar bounds it without refusing a real
* value. `targetVersion` is 1..256 characters, and the producer binds this schema (#3331), so an
* empty version is refused before it can match no fixture row. The 256 copies the unexported `VERSION_MAX` constant in `@zion/sdk-contracts`
* `app-admin-inputs.ts`, the bound the sibling app-admin contract puts on a version
* (zion-admin-app `uncovered-admin-routes` ruling R-007); `release.fixture_versions.version` is
* unbounded. It fixes no version grammar.
*
* Bound directly by `ChannelController.startCanary` in
* `cloud/dp/core-api/src/release/channel.controller.ts` since #3331.
*/
const startCanaryRequestSchema = z.strictObject({
	appId: pathParamSchema,
	targetVersion: z.string().min(1).max(256)
});
/** `POST /v1/release/channels/{id}/canary` response: the new canary's status, 201. */
const startCanaryResponseSchema = canaryStatusSchema;
/** `POST /v1/release/channels/{id}/canary/advance` response: the updated status, 201. */
const advanceCanaryResponseSchema = canaryStatusSchema;
/** `POST /v1/release/channels/{id}/canary/pause` response: the status, 201. No body on the way in. */
const pauseCanaryResponseSchema = canaryStatusSchema;
/** `POST /v1/release/channels/{id}/canary/resume` response: the status, 201. */
const resumeCanaryResponseSchema = canaryStatusSchema;
/** `POST /v1/release/channels/{id}/canary/rollback` response: the status, 201. */
const rollbackCanaryResponseSchema = canaryStatusSchema;
/**
* `GET /v1/release/channels/{id}/canary` response, when a canary matches. The route answers 204
* and no body otherwise, so a client reads absence from the status code.
*/
const getCanaryStatusResponseSchema = canaryStatusSchema;
/**
* The query of the canary status read and the four canary controls (#4450):
* `GET /v1/release/channels/{id}/canary` and `POST .../canary/advance`, `/pause`, `/resume`,
* `/rollback`.
*
* `appId` is the app slug, the value {@link canaryStatusSchema}'s `appId` and
* {@link startCanaryRequestSchema}'s `appId` carry, so `pathParamSchema`'s one-segment grammar
* bounds it. Optional: a client that names no app keeps the single-canary behaviour, and a control
* refuses with {@link canaryAppRequiredBodySchema} when two or more canaries are live. Any other
* key is refused, 400 on the server.
*
* Owner: `ChannelController` in `cloud/dp/core-api/src/release/channel.controller.ts`, which binds
* it with `@Query({ schema })` on the five routes.
*/
const canaryAppQuerySchema = z.strictObject({ appId: pathParamSchema.describe("the app slug of the live canary to act on").optional() });
/**
* The 409 body of a canary control that names no app while two or more canaries are live on the
* channel. The server changed nothing.
*
* `liveAppIds` lists the live canaries' app slugs in the order of {@link LIST_CANARIES_ROUTE}:
* `startedAt`, then `appId`. Send one of them as `?appId` to act on that canary. `error` equals
* `code`, which is what makes the server's exception filter send the body whole.
*/
const canaryAppRequiredBodySchema = z.object({
	statusCode: z.literal(409),
	error: z.literal("canary_app_required"),
	code: z.literal("canary_app_required"),
	liveAppIds: z.array(z.string()).min(2),
	hint: z.string(),
	message: z.union([z.string(), z.array(z.string())])
});
/**
* `GET /v1/release/channels/{id}/canaries` response: the channel's live canaries, one per app,
* ordered by `startedAt` then `appId`. An empty `canaries` when none is live.
*/
const listCanariesResponseSchema = z.object({ canaries: z.array(canaryStatusSchema) });
/**
* `GET /v1/release/customer-channels/app-bindings` response: every mapping this org holds, sorted
* by app slug. An app with no mapping has no row and follows the org's default channel.
*/
const listAppChannelBindingsResponseSchema = z.array(appChannelBindingSchema);
/**
* `PUT /v1/release/customer-channels/app-bindings/{appId}` body.
*
* The channel only. `ringName` is derived from the named channel server-side and is never accepted
* here: taking a ring from the body would let a caller name a ring the channel does not have,
* which is a tenancy control taking its input from the thing it controls. There is no
* `organizationId` either — the org comes from the verified JWT — and strictness is what makes an
* attempt to smuggle either one a 400 rather than a silently ignored member.
*/
const putAppChannelBindingRequestSchema = z.strictObject({ customerChannelId: z.uuid() });
/** `PUT /v1/release/customer-channels/app-bindings/{appId}` response: the resulting mapping. */
const putAppChannelBindingResponseSchema = appChannelBindingSchema;
/** Every route this file declares, for the package's route census. */
const channelsRoutes = Object.freeze([
	LIST_CHANNELS_ROUTE,
	CREATE_CHANNEL_ROUTE,
	UPDATE_CHANNEL_ROUTE,
	DELETE_CHANNEL_ROUTE,
	SET_CHANNEL_ASSIGNMENTS_ROUTE,
	START_CANARY_ROUTE,
	ADVANCE_CANARY_ROUTE,
	PAUSE_CANARY_ROUTE,
	RESUME_CANARY_ROUTE,
	ROLLBACK_CANARY_ROUTE,
	GET_CANARY_STATUS_ROUTE,
	LIST_CANARIES_ROUTE,
	GET_CHANNEL_ROUTE,
	LIST_APP_CHANNEL_BINDINGS_ROUTE,
	PUT_APP_CHANNEL_BINDING_ROUTE,
	DELETE_APP_CHANNEL_BINDING_ROUTE
]);
//#endregion
export { ADVANCE_CANARY_ROUTE, CREATE_CHANNEL_ROUTE, DELETE_APP_CHANNEL_BINDING_ROUTE, DELETE_CHANNEL_ROUTE, GET_CANARY_STATUS_ROUTE, GET_CHANNEL_ROUTE, LIST_APP_CHANNEL_BINDINGS_ROUTE, LIST_CANARIES_ROUTE, LIST_CHANNELS_ROUTE, PAUSE_CANARY_ROUTE, PUT_APP_CHANNEL_BINDING_ROUTE, RELEASE_APP_CHANNEL_BINDINGS_PATH, RELEASE_APP_CHANNEL_BINDING_PATH_TEMPLATE, RELEASE_CHANNELS_PATH, RELEASE_CHANNEL_ASSIGNMENTS_PATH_TEMPLATE, RELEASE_CHANNEL_CANARIES_PATH_TEMPLATE, RELEASE_CHANNEL_CANARY_ADVANCE_PATH_TEMPLATE, RELEASE_CHANNEL_CANARY_PATH_TEMPLATE, RELEASE_CHANNEL_CANARY_PAUSE_PATH_TEMPLATE, RELEASE_CHANNEL_CANARY_RESUME_PATH_TEMPLATE, RELEASE_CHANNEL_CANARY_ROLLBACK_PATH_TEMPLATE, RELEASE_CHANNEL_PATH_TEMPLATE, RESUME_CANARY_ROUTE, ROLLBACK_CANARY_ROUTE, SET_CHANNEL_ASSIGNMENTS_ROUTE, START_CANARY_ROUTE, UPDATE_CHANNEL_ROUTE, adminCustomerChannelSchema, advanceCanaryRequestSchema, advanceCanaryResponseSchema, appChannelBindingSchema, assignedGroupInfoSchema, canaryAppQuerySchema, canaryAppRequiredBodySchema, canaryStatusSchema, channelAppVersionSchema, channelGroupAssignmentSchema, channelHealthSchema, channelsRoutes, createChannelRequestSchema, createChannelResponseSchema, customerChannelRecordSchema, deleteChannelResponseSchema, getCanaryStatusResponseSchema, getChannelResponseSchema, listAppChannelBindingsResponseSchema, listCanariesResponseSchema, listChannelsResponseSchema, pauseCanaryResponseSchema, putAppChannelBindingRequestSchema, putAppChannelBindingResponseSchema, releaseAppChannelBindingPath, releaseChannelAssignmentsPath, releaseChannelCanariesPath, releaseChannelCanaryAdvancePath, releaseChannelCanaryPath, releaseChannelCanaryPausePath, releaseChannelCanaryResumePath, releaseChannelCanaryRollbackPath, releaseChannelPath, resumeCanaryResponseSchema, rollbackCanaryResponseSchema, setChannelAssignmentsRequestSchema, setChannelAssignmentsResponseSchema, startCanaryRequestSchema, startCanaryResponseSchema, updateChannelRequestSchema, updateChannelResponseSchema };

//# sourceMappingURL=channels.js.map